/**
 * Hjelpeside: slik slår du på «Nøyaktig posisjon» for nettleseren, så GPS-sporing
 * og fart/kurs fungerer. Lenkes til fra «Min seilas» og GPS-instrumentet.
 */
import { Link } from 'react-router-dom';
import { GsfLogo } from '../components/Logo.tsx';

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="shrink-0 w-6 h-6 rounded-full bg-slate-700 text-slate-100 text-xs font-bold flex items-center justify-center">
        {n}
      </span>
      <span className="pt-0.5">{children}</span>
    </li>
  );
}

export function HelpLocationPage() {
  return (
    <div className="min-h-screen bg-slate-950">
      <div className="max-w-xl mx-auto px-4 pt-4 pb-12">
        <div className="flex items-center justify-between mb-4">
          <Link to="/" className="text-slate-400 text-sm">&larr; Forsiden</Link>
          <Link to="/" aria-label="Grimstad Seilforening">
            <GsfLogo size="sm" />
          </Link>
        </div>

        <h1 className="text-2xl font-bold mb-1">📍 Slik får du nøyaktig posisjon</h1>
        <p className="text-sm text-slate-400 mb-5">
          For GPS-sporing og fart/kurs må telefonen gi <strong>nøyaktig</strong> (GPS-)posisjon.
          Hvis nøyaktigheten viser <strong>±1000–2000 m</strong> i stedet for <strong>±5–20 m</strong>,
          er det nesten alltid innstillingen «Nøyaktig posisjon» for nettleseren som mangler.
        </p>

        {/* Test */}
        <Link
          to="/instrument"
          className="flex items-center justify-between bg-sky-900/30 border border-sky-800 rounded-xl p-4 mb-6"
        >
          <div>
            <div className="font-semibold text-sky-200">🧭 Test posisjonen din</div>
            <div className="text-xs text-slate-400">
              Åpner GPS-instrumentet. Se «Nøyaktighet» nederst: ±5–20 m = bra.
            </div>
          </div>
          <span className="text-sky-300">&rarr;</span>
        </Link>

        {/* Android */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 mb-4">
          <h2 className="font-semibold mb-3">🤖 Android (Samsung, Chrome)</h2>
          <ol className="space-y-3 text-sm text-slate-200">
            <Step n={1}>
              Sveip ned varslingspanelet og sjekk at <strong>Posisjon</strong> (GPS) er <strong>på</strong>.
            </Step>
            <Step n={2}>
              Åpne <strong>Innstillinger</strong> → <strong>Apper</strong> → <strong>Chrome</strong>.
            </Step>
            <Step n={3}>
              Trykk <strong>Tillatelser</strong> → <strong>Posisjon</strong> → velg{' '}
              <strong>«Tillat mens appen er i bruk»</strong>.
            </Step>
            <Step n={4}>
              Slå <strong>PÅ</strong> bryteren <strong>«Bruk nøyaktig posisjon»</strong> nederst.
              <span className="block text-xs text-amber-300 mt-1">
                Dette er den viktigste — uten den får du bare ~2 km nøyaktighet.
              </span>
            </Step>
            <Step n={5}>
              (Valgfritt, gir raskere fix) <strong>Innstillinger</strong> → <strong>Posisjon</strong> →{' '}
              <strong>Posisjonstjenester</strong> → slå på <strong>«Google nøyaktig posisjon»</strong>.
            </Step>
          </ol>
        </div>

        {/* iPhone */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 mb-4">
          <h2 className="font-semibold mb-3">🍎 iPhone (Safari)</h2>
          <ol className="space-y-3 text-sm text-slate-200">
            <Step n={1}>
              <strong>Innstillinger</strong> → <strong>Personvern og sikkerhet</strong> →{' '}
              <strong>Stedstjenester</strong> → slå <strong>på</strong>.
            </Step>
            <Step n={2}>
              Bla ned og velg nettleseren (<strong>Safari-nettsteder</strong> eller{' '}
              <strong>Chrome</strong>) → velg <strong>«Mens appen brukes»</strong>.
            </Step>
            <Step n={3}>
              Slå <strong>PÅ</strong> <strong>«Nøyaktig posisjon»</strong>.
            </Step>
            <Step n={4}>
              Last regattasiden på nytt og tillat posisjon når nettleseren spør.
            </Step>
          </ol>
        </div>

        {/* Under seilasen */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
          <h2 className="font-semibold mb-3">⛵ Under seilasen</h2>
          <ul className="space-y-2 text-sm text-slate-200 list-disc pl-5">
            <li>Ha regattasiden <strong>åpen</strong> med <strong>skjermen på</strong> (appen holder skjermen våken).</li>
            <li>Ha telefonen <strong>oppe på båten</strong> med fri sikt til himmelen — ikke i en bag eller under dekk.</li>
            <li>Slå på <strong>«Send posisjon»</strong> i «Min seilas» og la den stå på.</li>
            <li>Du trenger <strong>mobildekning</strong> der du seiler.</li>
            <li>Sporingen stopper av seg selv når båten er <strong>registrert i mål</strong>.</li>
          </ul>
        </div>

        <p className="text-xs text-slate-500 text-center mt-6">
          Fortsatt ±1000 m etter dette? Sjekk at du er <strong>utendørs</strong>, og gi GPS-en
          20–30 sekunder på å låse første gang.
        </p>
      </div>
    </div>
  );
}
